import React, { useMemo } from "react";
import "style/MarksimInsights.scss";

import ui from "config/i18n/ui";
import useLang from "hooks/useLang";

import Reveal from "components/Reveal";

// "MARKSIM in practice": four illustrations of a study, redrawn from the
// 2017 trade-fair deck (choice task, utility evaluation, joint preference
// map, segment market shares). Neutral products A–F, illustrative data.

type Copy = typeof ui.en.insights;
type AttrKey = keyof Copy["attrs"];

const PRODUCTS = ["A", "B", "C", "D", "E", "F"];

// -- 01 Choice task ----------------------------------------------------------

const CHOICE_ROWS: [AttrKey, string, string][] = [
  ["price", "699 €", "849 €"],
  ["battery", "18 h", "30 h"],
  ["camera", "12 MP", "48 MP"],
  ["storage", "128 GB", "256 GB"],
  ["display", "LCD", "OLED"],
];

const ChoiceTask = ({ t }: { t: Copy }) => (
  <div className="mi-choice">
    <div className="mi-choice__top">
      <span>{t.choice.progress}</span>
      <span className="mi-choice__bar" aria-hidden="true">
        <span style={{ width: "25%" }} />
      </span>
    </div>
    <p className="mi-choice__q">{t.choice.question}</p>
    <table className="mi-choice__table">
      <thead>
        <tr>
          <th aria-hidden="true" />
          <th>{`${t.product} A`}</th>
          <th>{`${t.product} B`}</th>
        </tr>
      </thead>
      <tbody>
        {CHOICE_ROWS.map(([key, a, b]) => (
          <tr key={key}>
            <th scope="row">{t.attrs[key]}</th>
            <td>{a}</td>
            <td>{b}</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td />
          <td>
            <span className="mi-choice__btn">{t.choice.choose}</span>
          </td>
          <td>
            <span className="mi-choice__btn is-selected">
              {t.choice.choose}
            </span>
          </td>
        </tr>
      </tfoot>
    </table>
  </div>
);

// -- 02 Utility evaluation ---------------------------------------------------

const IMPORTANCE: [AttrKey, number][] = [
  ["price", 31],
  ["battery", 22],
  ["camera", 18],
  ["storage", 12],
  ["display", 9],
  ["brand", 8],
];

const Importance = ({
  t,
  percent,
}: {
  t: Copy;
  percent: Intl.NumberFormat;
}) => (
  <div className="mi-bars">
    <p className="mi-bars__axis">{t.importance}</p>
    {IMPORTANCE.map(([key, value]) => (
      <div className="mi-bars__row" key={key}>
        <span className="mi-bars__label">{t.attrs[key]}</span>
        <span className="mi-bars__track" aria-hidden="true">
          <span style={{ width: `${(value / 31) * 100}%` }} />
        </span>
        <span className="mi-bars__value">{percent.format(value / 100)}</span>
      </div>
    ))}
  </div>
);

// -- 03 Joint preference map (MDS + clusters) -------------------------------

interface Segment {
  x: number;
  y: number;
  sx: number;
  sy: number;
  n: number;
  color: string;
}

const SEGMENTS: Segment[] = [
  { x: 175, y: 118, sx: 46, sy: 30, n: 72, color: "#0562ff" },
  { x: 330, y: 104, sx: 42, sy: 28, n: 63, color: "#22a866" },
  { x: 150, y: 282, sx: 40, sy: 32, n: 54, color: "#7b61ff" },
  { x: 355, y: 284, sx: 40, sy: 27, n: 60, color: "#f0a020" },
  { x: 438, y: 196, sx: 25, sy: 30, n: 51, color: "#f0525f" },
];

const MAP_PRODUCTS: [string, number, number][] = [
  ["A", 205, 136],
  ["B", 312, 90],
  ["C", 372, 262],
  ["D", 132, 306],
  ["E", 268, 196],
  ["F", 446, 222],
];

const MAP_ATTRS: [AttrKey, number, number][] = [
  ["camera", 150, 70],
  ["display", 360, 58],
  ["battery", 300, 150],
  ["storage", 70, 190],
  ["brand", 92, 352],
  ["price", 448, 300],
];

/** Deterministic pseudo-random numbers (mulberry32), so the map is stable. */
const rng = (seed: number) => () => {
  seed |= 0;
  seed = (seed + 0x6d2b79f5) | 0;
  let r = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
  return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
};

const PreferenceMap = ({ t }: { t: Copy }) => {
  const dots = useMemo(() => {
    const rand = rng(2017);
    const gauss = () =>
      Math.sqrt(-2 * Math.log(rand() + 1e-9)) * Math.cos(2 * Math.PI * rand());
    return SEGMENTS.flatMap((s, si) =>
      Array.from({ length: s.n }, (_, i) => ({
        key: `${si}-${i}`,
        x: s.x + gauss() * s.sx * 0.8,
        y: s.y + gauss() * s.sy * 0.8,
        color: s.color,
      })),
    );
  }, []);

  return (
    <div className="mi-map">
      <svg viewBox="0 0 520 400" aria-hidden="true">
        <line x1="260" y1="16" x2="260" y2="384" className="mi-map__axis" />
        <line x1="16" y1="200" x2="504" y2="200" className="mi-map__axis" />
        {SEGMENTS.map((s, i) => (
          <ellipse
            key={i}
            cx={s.x}
            cy={s.y}
            rx={s.sx * 2.05}
            ry={s.sy * 2.05}
            fill={s.color}
            fillOpacity={0.06}
            stroke={s.color}
            strokeOpacity={0.55}
            strokeDasharray="4 4"
          />
        ))}
        {dots.map((d) => (
          <circle
            key={d.key}
            cx={d.x}
            cy={d.y}
            r={2.6}
            fill={d.color}
            fillOpacity={0.45}
          />
        ))}
        {MAP_ATTRS.map(([key, x, y]) => (
          <g key={key} className="mi-map__attr">
            <rect
              x={x - 3.5}
              y={y - 3.5}
              width={7}
              height={7}
              transform={`rotate(45 ${x} ${y})`}
            />
            <text x={x + 9} y={y + 4}>
              {t.attrs[key]}
            </text>
          </g>
        ))}
        {SEGMENTS.map((s, i) => (
          <g key={i} className="mi-map__seg">
            <circle cx={s.x} cy={s.y} r={10} fill={s.color} />
            <text x={s.x} y={s.y + 4}>
              {i + 1}
            </text>
          </g>
        ))}
        {MAP_PRODUCTS.map(([name, x, y]) => (
          <g key={name} className="mi-map__product">
            <circle cx={x} cy={y} r={5} />
            <text x={x + 8} y={y - 6}>
              {name}
            </text>
          </g>
        ))}
      </svg>
      {/* annotations as HTML so translated text wraps naturally */}
      <span className="mi-map__note mi-map__note--sensitive">
        {t.map.priceSensitive}
      </span>
      <span className="mi-map__note mi-map__note--secondary">
        {t.map.priceSecondary}
      </span>
    </div>
  );
};

// -- 04 Simulated market shares per segment ---------------------------------

const SEG_SIZE = [24, 21, 18, 20, 17];
const SHARES = [
  [34, 22, 14, 12, 10, 8],
  [12, 30, 25, 13, 12, 8],
  [8, 10, 9, 48, 15, 10],
  [10, 12, 30, 4, 18, 26],
  [9, 14, 22, 5, 20, 30],
];
const TOTAL = PRODUCTS.map((_, p) =>
  Math.round(
    SHARES.reduce((sum, row, s) => sum + (row[p] * SEG_SIZE[s]) / 100, 0),
  ),
);
const PRODUCT_COLORS = [
  "#0562ff",
  "#4f8dff",
  "#8fb9ff",
  "#1c2284",
  "#7a86b8",
  "#c9d2ea",
];

const Shares = ({ t, percent }: { t: Copy; percent: Intl.NumberFormat }) => {
  const rows = [
    ...SHARES.map((row, i) => ({
      label: `${t.segment} ${i + 1}`,
      size: percent.format(SEG_SIZE[i] / 100),
      row,
    })),
    { label: t.total, size: "", row: TOTAL },
  ];
  return (
    <div className="mi-shares">
      {rows.map((r, i) => (
        <div
          className={`mi-shares__row${i === rows.length - 1 ? " is-total" : ""}`}
          key={r.label}
        >
          <span className="mi-shares__label">
            {r.label}
            {r.size ? <small>{r.size}</small> : null}
          </span>
          <span className="mi-shares__bar">
            {r.row.map((v, p) => (
              <span
                key={p}
                style={{ width: `${v}%`, background: PRODUCT_COLORS[p] }}
                className={
                  [
                    p === 2 || p === 5 ? "is-light" : "",
                    v < 15 ? "is-small" : "",
                  ]
                    .filter(Boolean)
                    .join(" ") || undefined
                }
                title={`${t.product} ${PRODUCTS[p]}: ${percent.format(v / 100)}`}
              >
                {v >= 9 ? percent.format(v / 100) : ""}
              </span>
            ))}
          </span>
        </div>
      ))}
      <div className="mi-shares__legend">
        {PRODUCTS.map((p, i) => (
          <span key={p}>
            <i style={{ background: PRODUCT_COLORS[i] }} />
            {`${t.product} ${p}`}
          </span>
        ))}
      </div>
    </div>
  );
};

// -- Section -------------------------------------------------------------------

const MarksimInsights = () => {
  const lang = useLang();
  const t = ui[lang].insights;
  const percent = useMemo(
    () =>
      new Intl.NumberFormat(lang === "zh" ? "zh-Hans" : lang, {
        style: "percent",
        maximumFractionDigits: 0,
      }),
    [lang],
  );
  const figures = [
    <ChoiceTask t={t} />,
    <Importance t={t} percent={percent} />,
    <PreferenceMap t={t} />,
    <Shares t={t} percent={percent} />,
  ];

  return (
    <section className="insights">
      <div className="shell">
        <Reveal className="section-head">
          <h2>
            {t.title[0]} <span className="accent">{t.title[1]}</span>
          </h2>
          <p className="section-head__lead">{t.lead}</p>
        </Reveal>

        <div className="insights__grid">
          {t.steps.map((step, i) => (
            <Reveal key={step.kicker} className="insights__cell" delay={i * 80}>
              <figure className="insights__card">
                <div className="insights__figure">{figures[i]}</div>
                <figcaption>
                  <span className="insights__kicker">
                    {String(i + 1).padStart(2, "0")} · {step.kicker}
                  </span>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>

        <p className="insights__note">{t.note}</p>
      </div>
    </section>
  );
};

export default MarksimInsights;
